import { CheckOutlined, DeleteOutlined, DoubleRightOutlined, PlayCircleOutlined, SearchOutlined } from '@ant-design/icons'
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Avatar, Button, Flex, Input, List, Pagination } from 'antd'
import dayjs from 'dayjs'
import { useAttemptListener } from 'dev-masters-react-kit'
import { useEffect, useState } from 'react'
import { HiMiniNoSymbol } from 'react-icons/hi2'
import useSongsForTableSelector from '../../utils/hooks/useSongsForTableSelector'
import { attemptSearchSongs, Song } from '../../utils/redux/songsSlice'
import { listenerMiddleware } from '../../utils/redux/store'
import AudioFetcherPlayer from './AudioFetcherPlayer'

interface Props {
  onAdd: (song: Song) => void
  onDelete: (song: Song) => void
  playlistSongs?: Song[],
  setPlaylistSongs?: (songs: Song[]) => void
}

const handleDragStart = (event: React.PointerEvent) => {
  const isInteractiveElement = ['BUTTON', 'A', 'INPUT', 'TEXTAREA', 'SELECT'].includes(
    (event.target as HTMLElement).tagName
  );

  if (isInteractiveElement) {
    event.preventDefault();
  }
};

const DraggableItem = ({
  id,
  children,
}: {
  id: string;
  children: (handleProps: {
    listeners: ReturnType<typeof useSortable>['listeners'];
    attributes: ReturnType<typeof useSortable>['attributes'];
  }) => React.ReactNode;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ listeners, attributes })}
    </div>
  );
};

export default function PlaylistSongsFormItem({
  onAdd,
  onDelete, 
  playlistSongs,
  setPlaylistSongs,
}: Props) {
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10
  }) // Default pagination state

  const [playingSong, setPlayingSong] = useState<Song | undefined>(undefined)

  const [q, setQ] = useState('')

  useEffect(() => {
    fetchSongs(pagination.page, pagination.pageSize, q)
  }, [pagination, q])

  const { songs, total, fetchSongs } = useSongsForTableSelector()
  const playlistSongsWithId = playlistSongs?.map((song) => ({
    ...song,
    id: song._id,
  })) || [];

  const isLoading = useAttemptListener({
    listenerMiddleware: listenerMiddleware,

    attempt: attemptSearchSongs,
  })

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id && setPlaylistSongs) {
      const oldIndex = playlistSongsWithId.findIndex((s) => s.id === active.id);
      const newIndex = playlistSongsWithId.findIndex((s) => s.id === over.id);
      const moved = arrayMove(playlistSongsWithId, oldIndex, newIndex)
      setPlaylistSongs(moved);
    }
  };

  const onAddAll = () => {
    if (playlistSongs) {
      for (const song of songs) {
        if (!playlistSongs.some((s) => s._id === song._id)) {
          onAdd(song);
        }
      }
    }
  };

  return (
    <>
      <Flex align="start" gap="middle" style={{ width: '100%' }}>
        <List
          style={{ width: '100%', flexGrow: 1 }}
          header={
            <Flex justify="space-between" align="center" gap={10} style={{ width: '100%' }}>
              <div>All songs</div>
              <Input 
                placeholder="Search songs" 
                allowClear={true}
                prefix={<SearchOutlined />}
                onChange={(e) => {
                  setQ(e.target.value)
                  setPagination((prev) => ({ ...prev, page: 1 }))
                }}
              />
              <Button type='text' onClick={onAddAll} disabled={isLoading}>
                <DoubleRightOutlined />
              </Button>
            </Flex>
          }
          footer={
            <Pagination
              current={pagination.page}
              onChange={(page, pageSize) => setPagination({ page, pageSize })}
              defaultCurrent={1}
              total={total} />
          }
          bordered
          dataSource={songs}
          renderItem={(item) => (
            <List.Item>
              <Flex justify="space-between" align="center" gap={10} style={{ width: '100%' }}>
                <Avatar shape="square" src={item.picture} icon={<HiMiniNoSymbol />} />
                <div style={{ flexGrow: 1 }}>
                  <div>{item.title}</div>
                  <div>{item.artist}</div>
                </div>
                <div><i>{dayjs(item.createdAt).format("DD/MM/YYYY HH:mm:ss")}</i></div>
                <Button type='text' onClick={() => setPlayingSong(item)}>
                  <PlayCircleOutlined />
                </Button>
                {playlistSongs?.some((song) => song._id === item._id) ?
                  <Button type='text' disabled>
                    <CheckOutlined style={{ color: 'green' }} />
                  </Button>
                  :
                  <Button type='text' onClick={() => onAdd(item)}>
                    <DoubleRightOutlined />
                  </Button>
                }
              </Flex>
            </List.Item>
          )}
        />

        <DndContext 
          sensors={sensors} 
          collisionDetection={closestCenter} 
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={playlistSongsWithId}
            strategy={verticalListSortingStrategy}
          >
            <List
              style={{ width: '100%', flexGrow: 1 }}
              header={<div>This playlist songs</div>}
              bordered
              dataSource={playlistSongsWithId}
              renderItem={(item, i) => (
                <DraggableItem id={item.id}>
                  {({ listeners, attributes }) => (
                    <List.Item>
                      <Flex justify="space-between" align="center" gap={10} style={{ width: '100%' }}>
                        {/* Drag handle only: attach listeners/attributes here */}
                        <div
                          {...attributes}
                          {...listeners}
                          style={{ cursor: 'grab', padding: '0 8px', userSelect: 'none' }}
                        >
                          ⠿
                        </div>

                        <div>{i + 1}</div>

                        <Avatar
                          shape="square"
                          src={item.imageUrl}
                          icon={<HiMiniNoSymbol />}
                        />

                        <div style={{ flexGrow: 1 }}>
                          <div>{item.title}</div>
                          <div>{item.artist}</div>
                        </div>

                        <Button
                          type="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPlayingSong(item);
                          }}
                        >
                          <PlayCircleOutlined />
                        </Button>

                        <Button
                          type="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(item);
                          }}
                        >
                          <DeleteOutlined />
                        </Button>
                      </Flex>
                    </List.Item>
                  )}
                </DraggableItem>
              )}
            />
          </SortableContext>
        </DndContext>
      </Flex>
      <div style={{
        width: '500px',
        position: 'fixed',
        bottom: 0,
        left: 0,
        zIndex: 1000 }}>
        <AudioFetcherPlayer song={playingSong} setPlayingSong={setPlayingSong} />
      </div>
    </>
  )
}
